import { useParams } from 'react-router-dom'

import { Box, ColumnSplit, Link } from '@factorialco/deprecated-design-system'
import {
  WriteSurveyResponsePageDocument,
  WriteSurveyResponsePagePoliciesDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { questionGroupRespondent } from 'modules/core/store/collections/users'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import employees from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import UserBlock from 'modules/surveys/components/UserBlock'
import BlankSlate from 'modules/surveys/components/WriteResponse/BlankSlate'
import ResponseValues from 'modules/surveys/components/WriteResponse/ResponseValues'
import { useForm } from 'modules/surveys/hooks/useForm'
import { useQuestionGroup } from 'modules/surveys/hooks/useQuestionGroup'
import type Form from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

interface QuestionsProps {
  questionGroup: QuestionGroup
}

const Questions = ({ questionGroup }: QuestionsProps) => {
  if (!questionGroup.attributes.questions.length) {
    return <BlankSlate />
  }

  return <ResponseValues questionGroup={questionGroup} />
}

interface Props {
  questionGroupId: string
  form: Form
  jobTitle: string | null | undefined
  canEditSurveyForm: boolean
}

const Response = ({ form, questionGroupId, jobTitle, canEditSurveyForm }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { id: formId } = useParams()
  const { loading, questionGroup } = useQuestionGroup(questionGroupId)
  const user = questionGroup && questionGroupRespondent(questionGroup)
  const { navigation } = useNavigation()

  const hasWelcomeSpaceAccess =
    !!currentEmployee && !hasRegularAccess(currentEmployee.hasRegularAccess)

  if (loading) return <Loading />

  if (!questionGroup) {
    return <NotFound />
  }

  const back = hasWelcomeSpaceAccess
    ? navigation.employee_onboarding.onboardingTasks.fullPath()
    : canEditSurveyForm
      ? navigation.engagement.surveys.id.responses.fullPath({ id: String(formId) })
      : navigation.engagement.surveys.fullPath()

  return (
    <Box width="full" flexGrow>
      <ColumnSplit colored={false} minWidth="none">
        <PanesHeader
          illustration="surveys"
          title={form ? form.attributes.name : ''}
          description={i18n.t('surveys.form.response.description')}
          descriptionLink={<Link.Router to={back}>{i18n.t('common.go_back')}</Link.Router>}
        />
        <Box>
          {questionGroup && (
            <UserBlock
              user={!user ? null : user}
              description={jobTitle}
              isAnonymous={form.isAnonymous}
            >
              <Questions questionGroup={questionGroup} />
            </UserBlock>
          )}
        </Box>
      </ColumnSplit>
    </Box>
  )
}

export default function WriteSurveyResponsePage() {
  const { id: formId, question_group_id } = useParams()
  const { loading, form } = useForm(String(formId))
  const { loading: loadingQuestionGroup, questionGroup } = useQuestionGroup(
    String(question_group_id)
  )

  const { loading: loadingEmployeesCollection } = useAsyncData(
    () => employees.fetch({ withCachedFetchParams: true }),
    []
  )

  const { data: policiesData } = useQuery(WriteSurveyResponsePagePoliciesDocument, {
    variables: { surveyId: formId ?? '' },
    skip: !formId,
  })

  const canEditSurveyForm = policiesData?.surveys?.survey?._policies?.edit?.can ?? false

  const user = questionGroup && questionGroupRespondent(questionGroup)
  const employee =
    user && !user.isNew
      ? employees.find({
          access_id: user.attributes.id,
        })
      : undefined
  const employeeId = employee?.attributes.id

  const { data: employeeData, loading: loadingEmployee } = useQuery(
    WriteSurveyResponsePageDocument,
    {
      // NOTE: this solution is not idea, we should aim to have one single query to fetch all the data
      // that is needed for the page
      variables: { employeeId: employeeId ?? '0' },
      skip: !employeeId,
    }
  )

  const jobTitle = employeeData?.employees.employee?.referenceContract?.jobTitle

  return (
    <ResponsivePage.Body
      loading={loading || loadingQuestionGroup || loadingEmployee || loadingEmployeesCollection}
      showHeader={false}
      error={null}
    >
      {form && (
        <Response
          questionGroupId={String(question_group_id)}
          form={form}
          jobTitle={jobTitle}
          canEditSurveyForm={canEditSurveyForm}
        />
      )}
    </ResponsivePage.Body>
  )
}
